import React, { FC, useEffect, useState } from 'react';
import {
  BasicContainer,
  LinkAsButton,
  FlexCol,
  H2Text,
  Loader,
  PrimaryButton,
  GridDynamic,
  FlexDynamic,
  FlexRow,
} from '@minecraft.atoms';
import { useTranslation } from '@blocs.i18n';
import { useLogout } from '@blocs.auth';
import { WindowWidthContext } from '@minecraft.utils';
import { PlanTermEnum } from '@minecraft.graphql-operations';
import { PAYWALL_STEPS } from '../enums/paywall';
import { WalletLoader } from './WalletLoader';
import { CheckoutPlans } from './CheckoutPlans';
import { PaymentLoader } from './PaymentLoader';
import { PaywallLocation } from '../enums/PaywallLocation';
import { CheckoutProps } from './types';
import { Purchase } from '../types';
import { UnlimitedMediaUpsell } from './UnlimitedMediaUpsell';
import { useRegistrationLocation } from '../useRegistrationLocation';
import { usePaywallFunnelEvents } from '../usePaywallFunnelEvents';
import { DesktopRegistrationUpsell } from './DesktopRegistrationUpsell';
import { MediaPurchase } from '../enums/plans';
import { CollapsiblePurchase } from './CollapsiblePurchase';
import { TestimonialCarousel } from '../PlanSelection/TestimonialCarousel';
import { InfiniteBrandSroller } from './InfiniteBrandSroller';
import { AddPayment } from './AddPayment';
import { PaywallBanner } from '../PlanSelection/PaywallBanner';
import { CommercialCards } from './CommercialCards';
import { CheckoutHead } from './CheckoutHead';

export const Checkout: FC<CheckoutProps> = ({
  addPaymentError,
  addPaymentLoading,
  applyCouponError,
  closeBillingPopup,
  completePurchaseError,
  completePurchaseLoading,
  couponLoading,
  countryCode,
  currencyCode,
  currentPlanCode,
  currentPurchase,
  enableAnnualRenewal,
  getTitle = () => null,
  isAnnual,
  isModal,
  isWithinTrialPeriod,
  onAddPaymentOption,
  onUpdatePaymentOption,
  onApplyCoupon,
  onBack,
  onCompletePurchase,
  onPaymentCompleteRedirect,
  onRemoveCoupon,
  onSelectPaymentOption,
  onThreeDActionError,
  onNext,
  paywallLocation,
  plans,
  plansError,
  plansLoading,
  previewMediaPurchase,
  previewMediaPurchaseLoading,
  selectedPaymentOption,
  selectedPlan,
  setSelectedPlan,
  setIsAnnual,
  setShowAddPaymentMethod = () => null,
  showAddPaymentMethod,
  startingStep,
  threeDActionContext,
  wallet,
  walletLoading,
  isAnnualToggleLocked,
  currentProfile,
  showApplePayButton,
  onClickApplePay,
  requestRecurlyTokenAndValidate,
  handlePaymentPriorityChange,
  paymentMethodFormRef,
  paymentMethodFormikRef,
  modalVariant,
  discountType,
}) => {
  const { t } = useTranslation();
  const isMobile = WindowWidthContext.useIsMobile();
  const [logout] = useLogout(false);
  const [showPaymentMethods, setShowPaymentMethods] = useState<boolean>(false);
  const { isMobileRegistration, isRegistration, isDesktopRegistration } = useRegistrationLocation({ paywallLocation });
  const signupFunnelEvents = usePaywallFunnelEvents(paywallLocation);
  const startsAtCheckoutMobile = startingStep === PAYWALL_STEPS.CHECKOUT && isMobile;
  const disableTrial = Boolean((currentPurchase as Purchase)?.coupon?.code);
  const purchaseableItems = previewMediaPurchase?.items;
  const showMobilePaymentForm = isMobile && (showPaymentMethods || !startsAtCheckoutMobile);
  const showBackButton = showMobilePaymentForm && !isRegistration && !purchaseableItems;
  const showPlansAndPaymentForm = showPaymentMethods || !isMobile || !startsAtCheckoutMobile;
  const showPlans = !(isRegistration || isMobile) || !!purchaseableItems;
  const showMobilePlans = isMobile && !isRegistration && !purchaseableItems;

  const handleBack = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    if (startsAtCheckoutMobile) {
      setShowPaymentMethods(false);
    } else {
      onBack && onBack();
    }
  };

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      event.preventDefault();
      signupFunnelEvents.trackCheckoutAbandoned();
      logout();
    };

    if (isRegistration) {
      window.addEventListener('popstate', handlePopState);
    }
    return () => {
      if (isRegistration) {
        window.removeEventListener('popstate', handlePopState);
      }
    };
  }, [isMobileRegistration, isRegistration, logout, signupFunnelEvents]);

  return (
    <FlexRow gap="3">
      {isDesktopRegistration && !isMobile && <InfiniteBrandSroller />}
      {isDesktopRegistration && !isMobile && selectedPlan?.isCardlessTrial && (
        <PaywallBanner
          title="common:paywall.banner.title.cardlessTrial"
          iconName="no-credit-card"
          data-testid="price-framing-banner"
          fontWeight="medium"
        />
      )}
      <FlexDynamic
        data-testid="paywall-checkout-wrapper"
        bgColor="card"
        borderRadius="md"
        w="full"
        className={isModal ? 'cn_atom_p-0' : 'cn_atom_p-2'}
        mdProps={{
          className: 'cn_atom_p-4',
        }}
      >
        {paywallLocation === PaywallLocation.SIGNUP && !isRegistration && (
          <LinkAsButton onClick={handleBack} label={`< ${t('common:button.back')}`} className="cn_m-6" />
        )}
        {(!isMobile || !isModal) && !isRegistration && (
          <BasicContainer mx="1" my="2" className="cn_atom_px-8">
            <H2Text fontWeight="medium" as="h3" className="cn_w-full cn_md:w-auto mb-2">
              {getTitle()}
            </H2Text>
          </BasicContainer>
        )}
        <UnlimitedMediaUpsell
          show={isMobile && purchaseableItems?.length > 0}
          plans={plans}
          countryCode={countryCode}
          currencyCode={currencyCode}
        />
        <GridDynamic
          w="full"
          columns="1fr"
          mdProps={{
            columns: '1.2fr 1fr',
            gap: '8',
          }}
          gap="0"
          className={isModal ? 'cn_atom_p-3' : 'cn_atom_p-0'}
        >
          {showPlans && (
            <CheckoutPlans
              onNext={onNext}
              isAnnual={isAnnual}
              plans={plans}
              previewMediaPurchase={previewMediaPurchase}
              previewMediaPurchaseLoading={previewMediaPurchaseLoading}
              plansError={plansError}
              plansLoading={plansLoading}
              setIsAnnual={setIsAnnual}
              selectedPlan={selectedPlan}
              setSelectedPlan={setSelectedPlan}
              countryCode={countryCode}
              isPlanVisible={!isMobile || !!purchaseableItems}
              startingStep={startingStep}
              isModal={isModal}
              getTitle={getTitle}
              showPaymentMethods={showPaymentMethods}
              setShowPaymentMethods={setShowPaymentMethods}
              startsAtCheckoutMobile={startsAtCheckoutMobile}
              handleBack={handleBack}
              isHeaderHidden={showMobilePaymentForm}
              currentPlanCode={currentPlanCode}
              paywallLocation={paywallLocation}
              isWithinTrialPeriod={isWithinTrialPeriod}
              isAnnualToggleLocked={isAnnualToggleLocked}
            />
          )}
          {isDesktopRegistration && (
            <DesktopRegistrationUpsell
              isAnnual={isAnnual}
              setIsAnnual={setIsAnnual}
              isToggleLocked={selectedPlan?.billingCode === MediaPurchase.alacarte}
              plans={plans}
              countryCode={countryCode}
              currencyCode={previewMediaPurchase?.currency?.code}
              selectedPlan={selectedPlan}
              disableTrial={disableTrial}
              onCompletePurchase={onCompletePurchase}
              onPaymentCompleteRedirect={onPaymentCompleteRedirect}
              setSelectedPlan={setSelectedPlan}
              discountType={discountType}
            />
          )}
          {showPlansAndPaymentForm && (
            <Loader loading={completePurchaseLoading} LoaderComponent={<PaymentLoader />}>
              <FlexCol gap="4">
                <Loader loading={walletLoading} LoaderComponent={<WalletLoader />}>
                  {showMobilePlans && (
                    <FlexCol w="full" gap="4" my="2">
                      <CheckoutHead
                        isAnnual={isAnnual}
                        setIsAnnual={setIsAnnual}
                        isToggleLocked={selectedPlan?.billingCode === MediaPurchase.alacarte || isAnnualToggleLocked}
                        purchaseableItems={previewMediaPurchase?.items}
                        plans={plans}
                        countryCode={countryCode}
                        currencyCode={previewMediaPurchase?.currency?.code}
                      />
                      <CollapsiblePurchase
                        plan={selectedPlan}
                        selectedPlan={selectedPlan}
                        currencyCode={selectedPlan?.currencyCode}
                        billingCode={selectedPlan?.billingCode}
                        setSelectedPlan={setSelectedPlan}
                        billed={selectedPlan?.billed}
                        countryCode={countryCode}
                        discountedPrice={Number(selectedPlan?.discountedPrice)}
                        price={Number(selectedPlan?.price)}
                        temperature={selectedPlan?.temperature}
                        title={selectedPlan?.title}
                        icon={selectedPlan?.icon}
                        isAnnual={selectedPlan?.term?.code === PlanTermEnum.Annual}
                        planTermCode={selectedPlan?.term?.code}
                      />
                    </FlexCol>
                  )}
                  <AddPayment
                    addPaymentError={addPaymentError}
                    addPaymentLoading={addPaymentLoading}
                    allowCancel={!!wallet?.length}
                    applyCouponError={applyCouponError}
                    completePurchaseError={completePurchaseError}
                    completePurchaseLoading={completePurchaseLoading}
                    countryCode={countryCode}
                    couponLoading={couponLoading}
                    currencyCode={currencyCode}
                    currentPurchase={currentPurchase as Purchase}
                    disableTrial={disableTrial}
                    isAnnual={isAnnual}
                    isRegistration={isRegistration}
                    onCancel={() => setShowAddPaymentMethod(false)}
                    onCompletePurchase={onCompletePurchase}
                    onAddPaymentOption={onAddPaymentOption}
                    onUpdatePaymentOption={onUpdatePaymentOption}
                    onApplyCoupon={onApplyCoupon}
                    onNext={onNext}
                    onPaymentCompleteRedirect={onPaymentCompleteRedirect}
                    onRemoveCoupon={onRemoveCoupon}
                    onSelectPaymentOption={onSelectPaymentOption}
                    onThreeDActionError={onThreeDActionError}
                    paywallLocation={paywallLocation}
                    plans={plans}
                    selectedPaymentOption={selectedPaymentOption}
                    selectedPlan={selectedPlan}
                    setSelectedPlan={setSelectedPlan}
                    setIsAnnual={setIsAnnual}
                    threeDActionContext={threeDActionContext}
                    wallet={wallet}
                    currentProfile={currentProfile}
                    showApplePayButton={showApplePayButton}
                    onClickApplePay={onClickApplePay}
                    requestRecurlyTokenAndValidate={requestRecurlyTokenAndValidate}
                    handlePaymentPriorityChange={handlePaymentPriorityChange}
                    paymentMethodFormRef={paymentMethodFormRef}
                    paymentMethodFormikRef={paymentMethodFormikRef}
                    showAddPaymentMethod={showAddPaymentMethod}
                    setShowAddPaymentMethod={setShowAddPaymentMethod}
                    discountType={discountType}
                    purchaseableItems={purchaseableItems}
                    closeBillingPopup={closeBillingPopup}
                    modalVariant={modalVariant}
                    isModal={isModal}
                    enableAnnualRenewal={enableAnnualRenewal}
                  />
                </Loader>
                {showBackButton && (
                  <FlexCol w="full" mt="2">
                    <PrimaryButton variant="secondary" label={t('common:button.back')} onClick={handleBack} />
                  </FlexCol>
                )}
                {isMobile && <TestimonialCarousel />}
              </FlexCol>
            </Loader>
          )}
        </GridDynamic>
      </FlexDynamic>
      {isDesktopRegistration && <CommercialCards />}
    </FlexRow>
  );
};
